import { useState } from 'react';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import {
  INDEXING_FILE_SIZE,
  INSTANCE_CATEGORY,
  MAX_FILE_NUMBER,
} from '../../../constants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import {
  fileNotJsonOrXml,
  fileTooBig,
  formatBytes,
  getFileFormData,
} from '../../../utils/utils';
import Modal from '../../sections/modal/Modal';
import ModalButtons from '../../sections/modal/ModalButtons';
import FileUpload from '../file-upload/FileUpload';

const IndexDocsButton = ({ name, category, onRefresh }) => {
  const { projectId, instanceId } = useParams();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [files, setFiles] = useState([]);
  const [formatError, setFormatError] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [modal, setModal] = useState(false);

  const toggleModal = () => setModal(!modal);

  const handleIndexDocuments = async () => {
    try {
      const bodyPayload = getFileFormData('document', files[0], files[0].name);

      await collectionApis.indexDocuments(
        projectId,
        instanceId,
        name,
        bodyPayload,
        category
      );
      showToast(TOAST_TYPE.SUCCESS, t('indexSuccess', { ns: 'notifications' }));
    } catch (error) {
      const errorMessage = (status) => {
        switch (status) {
          case 400:
            return t('indexDocsError', { ns: 'notifications' });
          case 404:
            return t('indexCollectionError', { ns: 'notifications' });
          case 500:
            return t('internalServerError', { ns: 'errors' });
          default:
            return t('unknown', { ns: 'errors', status: status || '' });
        }
      };
      showToast(TOAST_TYPE.ERROR, errorMessage(error?.response?.status));
    } finally {
      toggleModal();
      setFiles([]);
      onRefresh();
    }
  };

  const onDrop = (acceptedFiles) => {
    if (isEmpty(acceptedFiles) || files.length >= MAX_FILE_NUMBER) return;

    setSizeError(fileTooBig(acceptedFiles[0].size, INDEXING_FILE_SIZE.BYTES));
    setFormatError(fileNotJsonOrXml(acceptedFiles[0].type));

    if (
      !fileTooBig(acceptedFiles[0].size, INDEXING_FILE_SIZE.BYTES) &&
      !fileNotJsonOrXml(acceptedFiles[0].type)
    ) {
      setSizeError(false);
      setFormatError(false);
      setFiles([...files, ...acceptedFiles]);
    }
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const removeFile = () => setFiles([]);

  return (
    <>
      <Tooltip title={t('indexDocs', { ns: 'buttons' })} arrow>
        <IconButton aria-label="index docs" onClick={toggleModal} size="large">
          <FileUploadIcon />
        </IconButton>
      </Tooltip>
      <Modal
        openModal={modal}
        title={t('fileUpload', { ns: 'instance' })}
        handleClose={toggleModal}
        action={
          <ModalButtons
            actionProps={{
              disabled: files.length === 0,
            }}
            actionText={t('indexDocs', { ns: 'instance' })}
            onCancel={toggleModal}
            onAction={handleIndexDocuments}
          />
        }
      >
        <Box sx={{ mt: 2 }}>
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <FileUpload
              files={files}
              open={open}
              handleRemove={removeFile}
              formatError={formatError}
              sizeLimit={formatBytes(INDEXING_FILE_SIZE.BYTES, 0)}
              sizeError={sizeError}
              helperText={t('dropOneJsonOrXmlFile', { ns: 'instance' })}
              fileErrorMsg={t('onlyJsonXmlFile', {
                ns: 'descriptions',
              })}
            />
          </div>
        </Box>
      </Modal>
    </>
  );
};

IndexDocsButton.propTypes = {
  name: PropTypes.string.isRequired,
  category: PropTypes.oneOf(Object.values(INSTANCE_CATEGORY)).isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default IndexDocsButton;
